import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { BUTTON_LABELS, PERMISSIONS } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import {
  IStepTemplateData,
  IVerticalTabProps,
  PlatformModules,
} from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { VerticalTabsLayout } from "@mibuildweb/components";
import {
  usePermissions,
  useStepTemplates,
  useWorkOrderTemplate,
} from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { ConfigPageLayout } from "../../ConfigPageLayout";
import { stepTemplateSchema } from "../schemas/StepTemplate.schema";
import { WorkOrderStepDetailsTabView } from "../tabs/WorkOrderStepDetailsTabView";
import { WorkOrderStepDurationTabView } from "../tabs/WorkOrderStepDurationTabView";

export function ConfigWorkOrderStepDetails({
  module,
}: Readonly<{ module: PlatformModules }>) {
  const { params, goBack } = useRouter();
  const { workOrderId, stepId } = params;
  const theme = useTheme();
  const { hasPermission } = usePermissions();

  const isReadOnly =
    (!stepId && !hasPermission(PERMISSIONS.WORK_ORDER_TEMPLATE.CREATE)) ||
    (stepId && !hasPermission(PERMISSIONS.WORK_ORDER_TEMPLATE.UPDATE));

  const defaultValues: IStepTemplateData = {
    title: "",
    stepType: "",
    priorityOrder: 1,
    module,
    description: "",
    documentCategoryId: null,
    isRequired: true,
    duration: 0,
    emailType: "",
    displayConfig: {
      hasButton: false,
      title: "",
      content: "",
      buttonType: null,
      buttonLabel: "",
    },
    builderDurations: [],
  };

  const form = useForm<IStepTemplateData>({
    mode: "onChange",
    defaultValues,
    resolver: zodResolver(stepTemplateSchema),
  });

  const {
    formState: { isDirty, isValid },
    reset,
    watch,
    getValues,
    setValue,
  } = form;

  const { title, duration, builderDurations = [] } = watch();

  const { getWorkOrderTemplateDetails, workOrderTemplateDetails } =
    useWorkOrderTemplate({
      module,
    });
  const {
    createStepTemplate,
    updateStepTemplate,
    getStepTemplateDetails,
    getStepTemplatesByModule,
  } = useStepTemplates({
    reset,
    assignmentTemplateId:
      workOrderTemplateDetails?.assignmentTemplates?.[0]?.id,
  });

  const fetchAllSteps = async () => {
    const response = await getStepTemplatesByModule(
      module,
      workOrderTemplateDetails?.assignmentTemplates?.[0]?.id
    );
    setValue("priorityOrder", response.data.length + 1);
  };

  useEffect(() => {
    if (workOrderId) {
      getWorkOrderTemplateDetails(workOrderId);
    }
  }, []);

  useEffect(() => {
    if (stepId) {
      getStepTemplateDetails(stepId);
    }
    if (workOrderTemplateDetails?.assignmentTemplates?.[0]?.id) {
      fetchAllSteps();
    }
  }, [workOrderTemplateDetails]);

  const tabs = useMemo<IVerticalTabProps<IStepTemplateData>[]>(
    () => [
      {
        label: "Step Details",
        value: "step details",
        controlledFormViewComponent: WorkOrderStepDetailsTabView,
        isComplete: isValid,
        hasErrors: !isValid,
        extraTabProps: { workOrderTemplateDetails, isReadOnly },
      },
      {
        label: "Step Duration",
        value: "step duration",
        controlledFormViewComponent: WorkOrderStepDurationTabView,
        isComplete: !!duration || builderDurations.length > 0,
        extraTabProps: { isReadOnly },
      },
    ],
    [isValid, workOrderTemplateDetails, duration, builderDurations, isReadOnly]
  );

  const renderPageTitle = () => {
    const action = stepId ? "Edit" : "Create";
    return (
      <>
        {action} {workOrderTemplateDetails?.name} Step:{" "}
        <span style={{ color: theme.palette.primary.main }}>{title}</span>
      </>
    );
  };

  const handleSubmit = async () => {
    const data = getValues();
    if (stepId) {
      const response = await updateStepTemplate(data);
      if (response.data) {
        goBack();
      }
    } else {
      const response = await createStepTemplate(data);
      if (response.data) {
        goBack();
      }
    }
  };

  return (
    <ConfigPageLayout
      renderTitle={renderPageTitle}
      bottomActions={{
        cancel: {
          label: BUTTON_LABELS.CANCEL,
          onClick: () => {
            goBack();
          },
        },
        submit: {
          label: "Save Step Template",
          onClick: handleSubmit,
          disabled: !isDirty || !isValid || !!isReadOnly,
        },
      }}
    >
      <VerticalTabsLayout tabs={tabs} form={form} transparentBackground />
    </ConfigPageLayout>
  );
}
